import './styles/globals.css';
import './styles/components/ml-theme.css';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { initI18n } from './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { RegionalSettingsProvider } from './contexts/RegionalSettingsContext';
import { getAppInsightsConnectionString, loadRuntimeConfig } from './runtimeConfig';
import { initTelemetry } from './telemetry';
import reportWebVitals from './reportWebVitals';
import { applyColorScheme, readColorScheme } from './hooks/useColorScheme';
import { captureSignupAttribution } from './lib/signupAttribution';

// Before the first paint, not in a component: rendering light and correcting to
// dark a frame later is a flash the user sees. This line used to unconditionally
// REMOVE the class — there was no dark palette to apply it to.
applyColorScheme(readColorScheme());

// Also before the router exists: it reads mlc/ref off the signup link (or the OAuth
// callback's returnUrl) and strips them, so BrowserRouter only ever sees the clean URL.
captureSignupAttribution();

// This app publishes no service worker. A registration is scoped to the origin,
// not to us, so a visitor can still be carrying one — and it would go on serving
// a cached app shell that no deploy could ever reach, since there is no
// service-worker.js left for it to update from. Clearing it on boot is the only
// remote kill switch for that. `ready` is the wrong probe here: it never settles
// when no worker is active, whereas getRegistrations() always resolves.
if ('serviceWorker' in navigator) {
  void navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => registrations.forEach((registration) => void registration.unregister()))
    .catch(() => {});
}

const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
const rootElementNode = document.getElementById('root');
const root = createRoot(rootElementNode);

const bootstrap = async () => {
  // Translations now arrive over the network (one chunk per language) rather than
  // riding in the entry bundle, so the first render has to wait for them —
  // otherwise it paints raw keys for a frame. Parallel with the config fetch:
  // neither needs the other.
  // loadRuntimeConfig() still has to run and complete — telemetry and apiFetch
  // read it back through getRuntimeConfig() — the value just is not needed here
  // any more, now that there is no theme class to apply from it.
  await Promise.all([loadRuntimeConfig(), initI18n()]);
  initTelemetry(getAppInsightsConnectionString(), 'moneylights-web');
  const { default: App } = await import('./App');

  root.render(
    <BrowserRouter basename={baseUrl}>
      <AuthProvider>
        <RegionalSettingsProvider>
          <App />
        </RegionalSettingsProvider>
      </AuthProvider>
    </BrowserRouter>);

  // Pass a reporter to actually collect anything, e.g. reportWebVitals(console.log).
  reportWebVitals();
};

void bootstrap();
